import { AlertCircle, Check, LogOut, ShieldCheck, Smartphone } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  maCzynnik,
  pobierzCzynniki,
  potwierdzKod,
  rozpocznijWlaczanie,
  wylaczCzynnik,
  type CzynnikTotp,
  type RozpoczeteWlaczanie,
} from '../../lib/dwuskladnikowe';
import { useAuth } from '../../providers/AuthProvider';

// =============================================================================
// Karta „Uwierzytelnianie dwuskładnikowe" — jedna dla trzech stron ustawień
// (admin, partner, super-admin), bo czynnik jest cechą KONTA, nie panelu.
//
// Trzy stany karty: sprawdzam → wyłączone (przycisk „Włącz") → włączone
// (data, stan tej sesji, „Wyłącz"). Włączanie to okno z kodem QR i polem na
// sześć cyfr; wyłączanie wymaga kodu, gdy sesja jest aal1 (lib/dwuskladnikowe.ts).
// Obok: „Wyloguj na wszystkich urządzeniach" — jedyne miejsce, z którego
// wychodzi globalne wylogowanie (AuthProvider `signOutEverywhere`).
// =============================================================================

type Stan =
  | { rodzaj: 'sprawdzam' }
  | { rodzaj: 'wylaczone' }
  | { rodzaj: 'wlaczone'; czynnik: CzynnikTotp }
  | { rodzaj: 'wlaczam'; start: RozpoczeteWlaczanie }
  | { rodzaj: 'wylaczam'; czynnik: CzynnikTotp };

export interface KartaDwuskladnikoweProps {
  /** Z `?dwuskladnikowe=wymagane` — baner „konto wymaga 2FA" nad kartą. */
  wymuszone?: boolean;
}

export function KartaDwuskladnikowe({ wymuszone = false }: KartaDwuskladnikoweProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { stanMfa, odswiezMfa, signOutEverywhere } = useAuth();
  const [stan, setStan] = useState<Stan>({ rodzaj: 'sprawdzam' });
  const [kod, setKod] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const [komunikat, setKomunikat] = useState<string | null>(null);
  const [zajety, setZajety] = useState(false);

  const wczytaj = useCallback(async () => {
    try {
      const czynniki = await pobierzCzynniki();
      const zweryfikowany = czynniki.find((c) => c.status === 'verified');
      setStan(
        zweryfikowany ? { rodzaj: 'wlaczone', czynnik: zweryfikowany } : { rodzaj: 'wylaczone' }
      );
    } catch (err) {
      setBlad((err as Error).message);
      setStan({ rodzaj: 'wylaczone' });
    }
  }, []);

  useEffect(() => {
    void wczytaj();
  }, [wczytaj]);

  const zacznijWlaczanie = async () => {
    setBlad(null);
    setKomunikat(null);
    setZajety(true);
    try {
      const start = await rozpocznijWlaczanie();
      setKod('');
      setStan({ rodzaj: 'wlaczam', start });
    } catch (err) {
      setBlad((err as Error).message);
    } finally {
      setZajety(false);
    }
  };

  const potwierdzWlaczenie = async (e: React.FormEvent) => {
    e.preventDefault();
    if (stan.rodzaj !== 'wlaczam') return;
    setBlad(null);
    setZajety(true);
    try {
      await potwierdzKod(stan.start.factorId, kod);
      await odswiezMfa();
      await wczytaj();
      setKomunikat(t('dwuskladnikowe.wlaczone', 'Włączone'));
    } catch (err) {
      setBlad((err as Error).message);
    } finally {
      setZajety(false);
    }
  };

  const potwierdzWylaczenie = async (e: React.FormEvent) => {
    e.preventDefault();
    if (stan.rodzaj !== 'wylaczam') return;
    setBlad(null);
    setZajety(true);
    try {
      await wylaczCzynnik(stan.czynnik.id, stanMfa, kod);
      await odswiezMfa();
      await wczytaj();
      setKomunikat(t('dwuskladnikowe.wylaczone', 'Wyłączone'));
    } catch (err) {
      setBlad((err as Error).message);
    } finally {
      setZajety(false);
    }
  };

  const anuluj = () => {
    setBlad(null);
    setKod('');
    void wczytaj();
  };

  const wylogujWszedzie = async () => {
    const zgoda = window.confirm(
      t(
        'dwuskladnikowe.wylogujWszedziePotwierdz',
        'Zakończyć sesje na wszystkich urządzeniach? Trzeba będzie zalogować się ponownie wszędzie.'
      )
    );
    if (!zgoda) return;
    try {
      await signOutEverywhere();
      navigate('/login', { replace: true });
    } catch (err) {
      setBlad((err as Error).message);
    }
  };

  const dataWlaczenia = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(i18n.language);
  };

  const poleKodu = (id: string) => (
    <div className="field">
      <label htmlFor={id} className="field__label">
        {t('dwuskladnikowe.kod', 'Kod z aplikacji')}
      </label>
      <input
        id={id}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9 ]*"
        maxLength={7}
        value={kod}
        onChange={(e) => setKod(e.target.value)}
        required
        className="input text-center tracking-[0.4em]"
        placeholder="123 456"
      />
      <p className="text-xs text-aurora-subtle mt-1">
        {t('dwuskladnikowe.kodPodpowiedz', 'Sześć cyfr, kod zmienia się co 30 sekund.')}
      </p>
    </div>
  );

  return (
    <div className="card" data-testid="karta-dwuskladnikowe">
      <div className="card__head">
        <h2 className="card__title flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-aurora-brand" />
          {t('dwuskladnikowe.tytul', 'Uwierzytelnianie dwuskładnikowe (2FA)')}
          {stan.rodzaj === 'wlaczone' && (
            <span className="pill pill--active">
              <span className="pin" />
              {t('dwuskladnikowe.wlaczone', 'Włączone')}
            </span>
          )}
        </h2>
      </div>
      <div className="card__body space-y-4">
        {wymuszone && !maCzynnik(stanMfa) && (
          <div
            role="alert"
            data-testid="baner-wymuszenia"
            className="flex items-center gap-2 p-3 bg-aurora-warning/10 border border-aurora-warning/20 rounded-ids-md text-aurora-warning text-sm"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {t(
              'dwuskladnikowe.wymuszoneBaner',
              'Konto super-admina wymaga włączonego 2FA. Włącz je poniżej, żeby wejść do panelu.'
            )}
          </div>
        )}

        <p className="text-sm text-aurora-muted">
          {t(
            'dwuskladnikowe.opis',
            'Drugi składnik przy logowaniu: kod z aplikacji uwierzytelniającej (Google Authenticator, Microsoft Authenticator, 1Password). Chroni konto, gdy hasło wycieknie.'
          )}
        </p>

        {blad && (
          <div
            role="alert"
            className="flex items-center gap-2 p-3 bg-aurora-danger/10 border border-aurora-danger/20 rounded-ids-md text-aurora-danger text-sm"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {blad}
          </div>
        )}
        {komunikat && !blad && (
          <div
            role="status"
            className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-ids-md text-green-400 text-sm"
          >
            <Check className="w-4 h-4 flex-shrink-0" />
            {komunikat}
          </div>
        )}

        {stan.rodzaj === 'sprawdzam' && (
          <p className="text-sm text-aurora-subtle">
            {t('dwuskladnikowe.sprawdzam', 'Sprawdzam…')}
          </p>
        )}

        {stan.rodzaj === 'wylaczone' && (
          <div className="flex items-center justify-between gap-4 p-4 bg-aurora-bg-subtle rounded-ids-md">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-aurora-subtle" />
              <p className="text-aurora-text">{t('dwuskladnikowe.wylaczone', 'Wyłączone')}</p>
            </div>
            <button
              type="button"
              onClick={() => void zacznijWlaczanie()}
              disabled={zajety}
              className="btn btn--primary"
            >
              {t('dwuskladnikowe.wlacz', 'Włącz 2FA')}
            </button>
          </div>
        )}

        {stan.rodzaj === 'wlaczam' && (
          <form
            onSubmit={potwierdzWlaczenie}
            className="p-4 bg-aurora-bg-subtle rounded-ids-md space-y-4"
            data-testid="formularz-wlaczania"
          >
            <p className="text-sm text-aurora-text">
              {t(
                'dwuskladnikowe.krokQr',
                'Zeskanuj kod aplikacją uwierzytelniającą albo wpisz sekret ręcznie.'
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <img
                src={stan.start.qr}
                alt="QR"
                width={176}
                height={176}
                className="rounded-ids-md bg-white p-2 flex-shrink-0"
              />
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <p className="overline">{t('dwuskladnikowe.sekret', 'Sekret')}</p>
                  <code
                    className="block text-xs break-all select-all text-aurora-text"
                    data-testid="sekret-2fa"
                  >
                    {stan.start.sekret}
                  </code>
                </div>
                {poleKodu('kod-wlaczania')}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button type="button" onClick={anuluj} className="btn btn--ghost" disabled={zajety}>
                {t('dwuskladnikowe.anuluj', 'Anuluj')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={zajety}>
                {t('dwuskladnikowe.potwierdz', 'Potwierdź i włącz')}
              </button>
            </div>
          </form>
        )}

        {stan.rodzaj === 'wlaczone' && (
          <div className="flex items-center justify-between gap-4 p-4 bg-aurora-bg-subtle rounded-ids-md">
            <div className="flex items-center gap-3 min-w-0">
              <Smartphone className="w-5 h-5 text-green-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-aurora-text truncate">{stan.czynnik.nazwa}</p>
                <p className="text-sm text-aurora-subtle">
                  {t('dwuskladnikowe.wlaczonoOd', 'Włączone od {{data}}', {
                    data: dataWlaczenia(stan.czynnik.utworzony),
                  })}
                  {' · '}
                  {stanMfa.poziom === 'aal2'
                    ? t('dwuskladnikowe.sesjaPotwierdzona', 'Ta sesja podała kod.')
                    : t('dwuskladnikowe.sesjaNiepotwierdzona', 'Ta sesja nie podała jeszcze kodu.')}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setBlad(null);
                setKomunikat(null);
                setKod('');
                setStan({ rodzaj: 'wylaczam', czynnik: stan.czynnik });
              }}
              className="btn btn--secondary"
            >
              {t('dwuskladnikowe.wylacz', 'Wyłącz 2FA')}
            </button>
          </div>
        )}

        {stan.rodzaj === 'wylaczam' && (
          <form
            onSubmit={potwierdzWylaczenie}
            className="p-4 bg-aurora-bg-subtle rounded-ids-md space-y-4"
            data-testid="formularz-wylaczania"
          >
            <p className="text-sm text-aurora-text">
              {t('dwuskladnikowe.wylaczPotwierdz', 'Wpisz aktualny kod, żeby wyłączyć 2FA.')}
            </p>
            {poleKodu('kod-wylaczania')}
            <div className="flex flex-wrap gap-2 justify-end">
              <button type="button" onClick={anuluj} className="btn btn--ghost" disabled={zajety}>
                {t('dwuskladnikowe.anuluj', 'Anuluj')}
              </button>
              <button type="submit" className="btn btn--danger" disabled={zajety}>
                {t('dwuskladnikowe.wylaczOstatecznie', 'Wyłącz')}
              </button>
            </div>
          </form>
        )}

        <div className="flex items-center justify-between gap-4 p-4 bg-aurora-bg-subtle rounded-ids-md">
          <div className="flex items-center gap-3 min-w-0">
            <LogOut className="w-5 h-5 text-aurora-subtle flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-aurora-text">
                {t('dwuskladnikowe.wylogujWszedzie', 'Wyloguj na wszystkich urządzeniach')}
              </p>
              <p className="text-sm text-aurora-subtle">
                {t(
                  'dwuskladnikowe.wylogujWszedzieOpis',
                  'Kończy sesje na każdym urządzeniu, także tym. Użyj po zgubieniu telefonu albo laptopa.'
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void wylogujWszedzie()}
            className="btn btn--secondary"
            data-testid="wyloguj-wszedzie"
          >
            {t('dwuskladnikowe.wylogujWszedzie', 'Wyloguj na wszystkich urządzeniach')}
          </button>
        </div>
      </div>
    </div>
  );
}
